// features/Strategies/strategies.routes.ts
// The four heavy screens here are code-split, and MUST stay that way.
//
// This module is imported STATICALLY by app/routes/index.tsx, so anything it
// imports statically lands in the entry chunk that every page load downloads
// before it can render anything — even a user who never opens Strategies.
// Importing the components directly put 2.3 MB of source there (measured from
// the build sourcemap: `src/features/Strategies` was the single largest
// contributor to the 3.5 MB / 1 MB-gzipped entry chunk, ahead of every vendor
// library), and dragged recharts and jspdf in with it.
//
// `lazyRouteComponent` is what the rest of the app already uses for exactly
// this reason; these four were the ones that had been missed.
import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import {
  getUniversesQueryOptions,
} from '@biasafe.queries/securities-universes-data/securities-universes-data';
import {
  getUniverseSecurityItemsQueryOptions,
} from '@biasafe.queries/securities-universes-security-items/securities-universes-security-items';
import {
  getGetMetricsQueryOptions,
} from '@biasafe.queries/metrics/metrics';
import {
  getGetIndicatorsQueryOptions,
} from '@biasafe.queries/indicators/indicators';
import {
  getSectorsQueryOptions,
} from '@biasafe.queries/securities-classification-sectors/securities-classification-sectors';
import {
  getIndustriesQueryOptions,
} from '@biasafe.queries/securities-classification-industries/securities-classification-industries';
import {
  getSubIndustriesQueryOptions,
} from '@biasafe.queries/securities-classification-sub-industries/securities-classification-sub-industries';
import {
  getSecurityTypesQueryOptions,
} from '@biasafe.queries/securities-classification-security-types/securities-classification-security-types';
import {
  getListStrategiesQueryOptions,
} from '@biasafe.queries/strategies/strategies';
import {
  getListBacktestsQueryOptions,
} from '@biasafe.queries/backtests/backtests';
import type { ReportTab } from './features/Report/types';
import { authRoute } from '@/app/routes/roots';
import { StrategiesLayout } from '@/app/layouts/StrategiesLayout';
import { RouteErrorFallback } from '@/shared/components/RouteErrorFallback';

const TIME_24H = 24 * 60 * 60 * 1000;
const TIME_5M = 5 * 60 * 1000;
const referenceQueryOpts = {
  staleTime: TIME_24H,
  gcTime: TIME_24H,
  retry: false as const,
};
const dashboardQueryOpts = {
  staleTime: TIME_5M,
  gcTime: TIME_24H,
  retry: false as const,
};

export const strategiesRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'strategies',
  component: StrategiesLayout,

  // Prefetch:
  //   * Dashboard-critical: strategies + backtests, used to derive
  //     each card's status. Both are fast enough that we don't need to
  //     gate the dashboard render on them.
  //   * Wizard-warmup: universes + classifications + metrics +
  //     indicators. These are the inputs the Investment Universe &
  //     Benchmark step (Step 1) consumes. Without this warm-up the
  //     user lands on Step 1 cold and stares at a skeleton for several
  //     seconds; warming them here means by the time the user clicks
  //     New Strategy and navigates through the 3-step modal, every
  //     reference-data call has already resolved in React Query's
  //     cache and Step 1 paints with real data on the first render.
  //
  // get_universe_security_items intentionally stays at the
  // strategy-creation-form route — it returns the full 53M-row OHLCV
  // table on a cold ClickHouse cache (~12s) and isn't needed for
  // dashboard navigation. The lighter calls below are all sub-second
  // and benefit from being warm before the wizard opens.
  loader: ({ context: { queryClient } }) => {
    queryClient.prefetchQuery({
      ...getListStrategiesQueryOptions({ archived: false }),
      ...dashboardQueryOpts,
    });
    queryClient.prefetchQuery({
      ...getListBacktestsQueryOptions(),
      ...dashboardQueryOpts,
    });
    // Wizard reference-data prefetch — fires in the background; user
    // never waits on these for the dashboard itself.
    const q = referenceQueryOpts;
    queryClient.prefetchQuery({ ...getUniversesQueryOptions(), ...q });
    queryClient.prefetchQuery({ ...getGetMetricsQueryOptions(), ...q });
    queryClient.prefetchQuery({ ...getGetIndicatorsQueryOptions(), ...q });
    queryClient.prefetchQuery({ ...getSectorsQueryOptions(), ...q });
    queryClient.prefetchQuery({ ...getIndustriesQueryOptions(), ...q });
    queryClient.prefetchQuery({ ...getSubIndustriesQueryOptions(), ...q });
    queryClient.prefetchQuery({ ...getSecurityTypesQueryOptions(), ...q });
  },
});

export const strategiesDashboardRoute = createRoute({
  getParentRoute: () => strategiesRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./features/Dashboard/Dashboard'),
    'Dashboard',
  ),
  errorComponent: RouteErrorFallback,
});

export const smartStrategyDefinitionFormRoute = createRoute({
  getParentRoute: () => strategiesRoute,
  path: 'strategy-creation-form',
  component: lazyRouteComponent(
    () => import('./features/SmartStrategyDefinitonForm/SmartStrategyDefinitionForm'),
    'SmartStrategyDefinitionForm',
  ),
  errorComponent: RouteErrorFallback,

  // The cheap reference data (universes, sectors, classifications,
  // metrics, indicators) was already warmed by strategiesRoute when
  // the user landed on the dashboard, so by the time the form opens
  // those queries are typically `success` in the cache and render on
  // the first paint.
  //
  // We still need to fire `universe-security-items` here because:
  //   1) It returns the full 53M-row OHLCV table — too heavy to
  //      warm on the dashboard for users who never open the wizard.
  //   2) It's only used by Step 1's filtered-securities engine, not
  //      the dashboard.
  loader: ({ context: { queryClient } }) => {
    queryClient.prefetchQuery({
      ...getUniverseSecurityItemsQueryOptions(),
      ...referenceQueryOpts,
    });
  },
});

// Per-step child routes for the creation form. The form renders every step
// inside one component (visibility-toggled by `currentStep`) rather than via
// <Outlet/>, so these routes carry no component of their own — they exist
// purely so each wizard step is a distinct, deep-linkable URL. The form syncs
// `currentStep` <-> the active step route (see SmartStrategyDefinitionForm).
// `universe-selection` is defined in Steps/StartingUniverse/route.ts (kept for
// its existing component reference); these are its siblings.
const StepRouteAnchor = () => null;

export const rebalanceScheduleRoute = createRoute({
  getParentRoute: () => smartStrategyDefinitionFormRoute,
  path: 'rebalance-schedule',
  component: StepRouteAnchor,
});

export const securitySelectionStepRoute = createRoute({
  getParentRoute: () => smartStrategyDefinitionFormRoute,
  path: 'security-selection',
  component: StepRouteAnchor,
});

export const weightingMethodRoute = createRoute({
  getParentRoute: () => smartStrategyDefinitionFormRoute,
  path: 'weighting-method',
  component: StepRouteAnchor,
});

export const strategyReportRoute = createRoute({
  getParentRoute: () => strategiesRoute,
  path: '$strategyId/report',
  component: lazyRouteComponent(
    () => import('./features/Report/Report'),
    'Report',
  ),
  errorComponent: RouteErrorFallback,
  // Optional ?tier= lets a caller open the report on a specific top tier
  // (e.g. Edit on a backtested strategy jumps straight to Model Configuration).
  // Optional ?tab= opens the report on a specific tab (e.g. Soteria deep-links
  // to the section it just discussed — "risk" for attribution, etc.).
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    tier?: 'report' | 'config' | 'live';
    tab?: ReportTab;
  } => {
    const tier = search.tier;
    const tab = search.tab;
    const tabs: ReportTab[] = [
      'performance',
      'risk',
      'constituents',
      'turnover',
      'spec',
      'methodology',
    ];
    return {
      ...(tier === 'config' || tier === 'live' || tier === 'report'
        ? { tier }
        : {}),
      ...(tabs.includes(tab as ReportTab) ? { tab: tab as ReportTab } : {}),
    };
  },
});

// The standalone live-model page is superseded by the tiered report shell
// (Live Model / Backtest Report / Model Configuration). Redirect so old links
// and bookmarks land on the same Live Model tier as every other entry point,
// instead of a separate bare page — the duplication the user flagged.
export const liveModelRoute = createRoute({
  getParentRoute: () => strategiesRoute,
  path: '$strategyId/live/$liveModelId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/strategies/$strategyId/report',
      params: { strategyId: params.strategyId },
      search: { tier: 'live' },
    });
  },
  errorComponent: RouteErrorFallback,
});

export const benchmarkDetailRoute = createRoute({
  getParentRoute: () => strategiesRoute,
  path: 'benchmarks/$benchmarkId',
  component: lazyRouteComponent(
    () => import('./features/Benchmarks/BenchmarkDetailPage'),
    'BenchmarkDetailPage',
  ),
  errorComponent: RouteErrorFallback,
});
